import React from 'react';
import { Pill, Upload, MessageCircle, CheckCircle, Truck, ShoppingBag } from 'lucide-react';
import { Button } from '../components/ui/button';

const Pharmacy = () => {
  const handleUploadPrescription = () => {
    window.open('https://wa.me/917010200869?text=Hi,%20I%20would%20like%20to%20upload%20my%20prescription%20for%20medicine%20order', '_blank');
  };

  const howItWorks = [
    {
      icon: MessageCircle,
      title: 'Send Prescription via WhatsApp',
      description: 'Click the button below to send your prescription photo via WhatsApp to our pharmacy team',
      color: 'text-green-500',
      bgColor: 'bg-green-50',
    },
    {
      icon: CheckCircle,
      title: 'Pharmacist Verification',
      description: 'Our licensed pharmacists will verify your prescription and prepare your medicines',
      color: 'text-blue-500',
      bgColor: 'bg-blue-50',
    },
    {
      icon: Truck,
      title: 'Fast Delivery or Pickup',
      description: 'Choose home delivery or visit our pharmacy for quick pickup',
      color: 'text-purple-500',
      bgColor: 'bg-purple-50',
    },
  ];

  const features = [
    'Genuine medicines from authorized sources',
    'Competitive pricing',
    'Home delivery available',
    'Expert pharmacist consultation',
    'Emergency medicine availability 24/7',
    'Easy refill options',
  ];

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <section
        className="relative h-[500px] flex items-center justify-center"
        style={{
          backgroundImage:
            'url(https://static.prod-images.emergentagent.com/jobs/8194830b-3733-4683-8455-807cbf80703e/images/6b7859a3cf942b5c90cca097b28da6df135cafb41186b09af26391b9d23777c9.png)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-[#0A2540]/90 to-[#0A2540]/70" />
        <div className="relative z-10 max-w-4xl mx-auto px-4 text-center">
          <Pill className="h-16 w-16 text-white mx-auto mb-4" />
          <h1
            className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-4 tracking-tight"
            style={{ fontFamily: 'Outfit, sans-serif' }}
            data-testid="pharmacy-heading"
          >
            Online Pharmacy
          </h1>
          <p className="text-xl text-white/90 mb-8">
            Order your medicines online with a valid prescription
          </p>
          <Button
            onClick={handleUploadPrescription}
            className="bg-[#25D366] text-white hover:bg-[#25D366]/90 rounded-full px-8 py-6 text-lg font-semibold shadow-xl hover:shadow-2xl transition-all hover:scale-105"
            data-testid="upload-prescription-whatsapp-button"
          >
            <MessageCircle className="mr-2 h-5 w-5" />
            Send Prescription via WhatsApp
          </Button>
        </div>
      </section>

      <section className="py-16 px-4">
        <div className="max-w-6xl mx-auto">
          <h2
            className="text-3xl sm:text-4xl font-bold text-[#0A2540] mb-4 text-center"
            style={{ fontFamily: 'Outfit, sans-serif' }}
          >
            How It Works
          </h2>
          <p className="text-center text-[#475569] mb-12 text-lg">
            Get your medicines in 3 simple steps
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
            {howItWorks.map((step, index) => {
              const Icon = step.icon;
              return (
                <div
                  key={step.title}
                  className="relative"
                  data-testid={`how-it-works-step-${index + 1}`}
                >
                  <div className="text-center">
                    <div className="absolute -top-4 -left-4 bg-[#0EA5E9] text-white w-12 h-12 rounded-full flex items-center justify-center font-bold text-xl">
                      {index + 1}
                    </div>
                    <div className={`${step.bgColor} rounded-2xl p-8 h-full`}>
                      <Icon className={`h-12 w-12 ${step.color} mx-auto mb-4`} />
                      <h3
                        className="text-xl font-semibold text-[#0A2540] mb-3"
                        style={{ fontFamily: 'Outfit, sans-serif' }}
                      >
                        {step.title}
                      </h3>
                      <p className="text-[#475569]">{step.description}</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="bg-white rounded-2xl p-8 md:p-12 shadow-lg">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
              <div>
                <ShoppingBag className="h-12 w-12 text-[#0EA5E9] mb-4" />
                <h2
                  className="text-3xl font-bold text-[#0A2540] mb-4"
                  style={{ fontFamily: 'Outfit, sans-serif' }}
                >
                  Why Choose Our Pharmacy?
                </h2>
                <p className="text-[#475569] mb-6">
                  Our pharmacy is committed to providing you with the highest quality medicines and healthcare products.
                </p>
                <ul className="space-y-3">
                  {features.map((feature) => (
                    <li key={feature} className="flex items-start">
                      <CheckCircle className="h-5 w-5 text-[#0EA5E9] mr-3 flex-shrink-0 mt-0.5" />
                      <span className="text-[#475569]">{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="bg-[#0EA5E9]/5 rounded-xl p-8 border-l-4 border-[#0EA5E9]">
                <h3 className="text-xl font-semibold text-[#0A2540] mb-4">Need Help?</h3>
                <p className="text-[#475569] mb-6">
                  Our pharmacy team is available 24/7 to assist you with your medicine needs.
                </p>
                <div className="space-y-3">
                  <a href="tel:7010200869" data-testid="pharmacy-call-button">
                    <Button className="w-full bg-[#EF4444] hover:bg-[#DC2626] text-white rounded-full font-semibold">
                      Call Emergency Line
                    </Button>
                  </a>
                  <Button
                    onClick={handleUploadPrescription}
                    className="w-full bg-[#25D366] text-white hover:bg-[#25D366]/90 rounded-full"
                    data-testid="pharmacy-whatsapp-button"
                  >
                    <MessageCircle className="mr-2 h-4 w-4" />
                    Chat on WhatsApp
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-12 px-4 bg-gradient-to-r from-[#0A2540] to-[#0EA5E9]">
        <div className="max-w-4xl mx-auto text-center">
          <h2
            className="text-3xl font-bold text-white mb-4"
            style={{ fontFamily: 'Outfit, sans-serif' }}
          >
            Ready to Order?
          </h2>
          <p className="text-white/90 mb-6 text-lg">
            Send your prescription now and get your medicines delivered or ready for pickup
          </p>
          <Button
            onClick={handleUploadPrescription}
            className="bg-white text-[#0A2540] hover:bg-white/90 rounded-full px-8 py-6 text-lg font-semibold"
            data-testid="pharmacy-cta-button"
          >
            <MessageCircle className="mr-2 h-5 w-5" />
            Send Prescription Now
          </Button>
        </div>
      </section>
    </div>
  );
};

export default Pharmacy;